// 급여가져오기 배부 계산 — 사원 급여를 공수 비율(Pivot)로 프로젝트에 나누고,
// 기타노무비 총액을 프로젝트 급여 비중으로 배부한다. (순수 함수 — 테스트 대상)

export interface AllocRow {
  cd_pjt: string;
  nm_pjt: string;
  am_pay: number;
  am_etc: number;
  am_total: number;
}

const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param salaryRows  douzone 급여(사원별) [{ cd_emp, am_pay }]
 * @param pivotColumns 공수 Pivot 컬럼 — pjt_* 만 배부 대상
 * @param pivotRows    사원별 프로젝트 비율 [{ cd_emp, pjt_XXX: 비율 }]
 * @param totalEtc     기타노무비 총액
 * 비율 스케일(%/0~1/원공수) 무관 — 각 사원 행의 비율합으로 정규화해 배부한다.
 */
export function computeSalaryAllocation(
  salaryRows: any[],
  pivotColumns: { cd_column: string; nm_column: string }[],
  pivotRows: any[],
  totalEtc: number
): AllocRow[] {
  const payByEmp = new Map<string, number>(
    (salaryRows ?? []).map((s) => [String(s.cd_emp), num(s.am_pay)])
  );

  const pjtCols = (pivotColumns ?? []).filter((c) =>
    c.cd_column.startsWith("pjt_")
  );
  if (!pjtCols.length) return [];

  const projSalary: Record<string, number> = {};
  pjtCols.forEach((c) => (projSalary[c.cd_column] = 0));

  (pivotRows ?? []).forEach((r) => {
    const pay = payByEmp.get(String(r.cd_emp)) ?? 0;
    if (!pay) return;
    const rowSum = pjtCols.reduce((s, c) => s + num(r[c.cd_column]), 0);
    if (rowSum <= 0) return; // 비율 합이 0이면 배부 불가
    pjtCols.forEach((c) => {
      projSalary[c.cd_column] += (pay * num(r[c.cd_column])) / rowSum;
    });
  });

  const totalSalary = Object.values(projSalary).reduce((a, b) => a + b, 0);

  return pjtCols.map((c) => {
    const am_pay = projSalary[c.cd_column];
    const am_etc = totalSalary > 0 ? (num(totalEtc) * am_pay) / totalSalary : 0;
    return {
      cd_pjt: c.cd_column.replace(/^pjt_/, ""),
      nm_pjt: c.nm_column,
      am_pay,
      am_etc,
      am_total: am_pay + am_etc,
    };
  });
}

export interface SalaryDistribution {
  projects: { cd_pjt: string; nm_pjt: string; field: string }[];
  rows: any[]; // 담당자 행들 + 소계/기타노무비/총계 행
  projectTotals: AllocRow[]; // 프로젝트별 합계(메인 반영용)
}

/**
 * 노무비배부 표시용 — 담당자(행) × 프로젝트(열) 피벗 + 소계/기타노무비/총계 3행.
 *  - 담당자 행: 급여(총액) + 프로젝트별 배부액 + 합계(=급여)
 *  - 소계: 프로젝트별 급여 배부 합
 *  - 기타노무비: 기타노무비 총액을 소계(프로젝트 급여) 비중으로 배부
 *  - 총계: 소계 + 기타노무비
 */
export function computeSalaryDistribution(
  salaryRows: any[],
  pivotColumns: { cd_column: string; nm_column: string }[],
  pivotRows: any[],
  totalEtc: number
): SalaryDistribution {
  const pjtCols = (pivotColumns ?? []).filter((c) =>
    c.cd_column.startsWith("pjt_")
  );
  const projects = pjtCols.map((c) => ({
    cd_pjt: c.cd_column.replace(/^pjt_/, ""),
    nm_pjt: c.nm_column,
    field: c.cd_column,
  }));

  const payByEmp = new Map<string, number>(
    (salaryRows ?? []).map((s) => [String(s.cd_emp), num(s.am_pay)])
  );

  const projSalary: Record<string, number> = {};
  pjtCols.forEach((c) => (projSalary[c.cd_column] = 0));

  // 담당자 행
  const empRows: any[] = (pivotRows ?? []).map((r) => {
    const pay = payByEmp.get(String(r.cd_emp)) ?? 0;
    const rowSum = pjtCols.reduce((s, c) => s + num(r[c.cd_column]), 0);
    const row: any = { nm_emp: r.nm_emp, am_pay: pay };
    let rowTotal = 0;
    pjtCols.forEach((c) => {
      const v = pay > 0 && rowSum > 0 ? (pay * num(r[c.cd_column])) / rowSum : 0;
      row[c.cd_column] = v || null; // 0 은 '-'(빈칸) 표시
      rowTotal += v;
      projSalary[c.cd_column] += v;
    });
    row.am_total = rowTotal;
    return row;
  });

  const totalSalary = Object.values(projSalary).reduce((a, b) => a + b, 0);

  // 요약 3행 + 프로젝트별 합계
  const subtotal: any = { nm_emp: "소계", am_pay: null };
  const etcRow: any = { nm_emp: "기타노무비", am_pay: null };
  const grand: any = { nm_emp: "총계", am_pay: null };
  const projectTotals: AllocRow[] = pjtCols.map((c, i) => {
    const am_pay = projSalary[c.cd_column];
    const am_etc = totalSalary > 0 ? (num(totalEtc) * am_pay) / totalSalary : 0;
    subtotal[c.cd_column] = am_pay;
    etcRow[c.cd_column] = am_etc;
    grand[c.cd_column] = am_pay + am_etc;
    return {
      cd_pjt: projects[i].cd_pjt,
      nm_pjt: projects[i].nm_pjt,
      am_pay,
      am_etc,
      am_total: am_pay + am_etc,
    };
  });
  subtotal.am_total = totalSalary;
  etcRow.am_total = num(totalEtc);
  grand.am_total = totalSalary + num(totalEtc);

  return {
    projects,
    rows: [...empRows, subtotal, etcRow, grand],
    projectTotals,
  };
}
